// 拠点間の在庫移動 — Stocky parity v2 Step 3。新規作成・出庫は Business 限定。
// 降格後も履歴・入庫・下書き取消は利用できる。
// 出庫（移動元から引く）→ 入庫（移動先へ足す）の2段。分納受領できる。
// 🔴 Shopify の純正「転送」は作らない＝これは UreyukiBox 内の記録と、
// 両端で行う在庫調整（Step 1 と同じ経路）だけで成り立っている。
import { apiFetch } from "./client";
import {
  canonicalPublicBigIntId,
  type PublicBigIntIdInput,
} from "./inventory_adjustments";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type TransferStatus = "draft" | "in_transit" | "received" | "cancelled";

export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  draft: "下書き",
  in_transit: "移動中",
  received: "受領済み",
  cancelled: "取り消し",
};

export interface TransferItem {
  shopify_variant_id: string;
  sku: string | null;
  title: string | null;
  current_product_title?: string | null;
  current_variant_title?: string | null;
  /** 出庫する数量（出庫後は変わらない）。 */
  qty: number;
  /** これまでに受領した累計。 */
  received_qty: number;
  /** qty - received_qty。サーバーが計算して返す（画面で持ち直さない）。 */
  remaining_qty: number;
}

export interface Transfer {
  id: string;
  source_location_id: string | null;
  source_location_name: string | null;
  dest_location_id: string | null;
  dest_location_name: string | null;
  status: TransferStatus;
  note: string | null;
  shipped_at: string | null;
  received_at: string | null;
  items: TransferItem[];
}

export interface TransferSummary {
  id: string;
  source_location_id: string | null;
  source_location_name: string | null;
  dest_location_id: string | null;
  dest_location_name: string | null;
  status: TransferStatus;
  note: string | null;
  shipped_at: string | null;
  received_at: string | null;
  item_count: number;
}

function normalizeTransfer(value: unknown): Transfer {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory transfer response is invalid");
  }
  const transfer = value as Omit<
    Transfer,
    "id" | "source_location_id" | "dest_location_id" | "items"
  > & {
    id: unknown;
    source_location_id: unknown;
    dest_location_id: unknown;
    items: unknown;
  };
  if (!Array.isArray(transfer.items)) {
    throw new TypeError("inventory transfer items are invalid");
  }
  return {
    ...transfer,
    id: canonicalStockyPublicBigIntId(transfer.id),
    source_location_id:
      transfer.source_location_id === null
        ? null
        : canonicalStockyPublicBigIntId(transfer.source_location_id),
    dest_location_id:
      transfer.dest_location_id === null
        ? null
        : canonicalStockyPublicBigIntId(transfer.dest_location_id),
    items: transfer.items.map((value) => {
      if (value === null || typeof value !== "object") {
        throw new TypeError("inventory transfer item is invalid");
      }
      const item = value as Omit<TransferItem, "shopify_variant_id"> & {
        shopify_variant_id: unknown;
      };
      return {
        ...item,
        shopify_variant_id: canonicalPublicBigIntId(
          item.shopify_variant_id,
          "shopify_variant_id",
        ),
      };
    }),
  };
}

function normalizeTransferSummary(value: unknown): TransferSummary {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory transfer summary response is invalid");
  }
  const transfer = value as Omit<
    TransferSummary,
    "id" | "source_location_id" | "dest_location_id"
  > & {
    id: unknown;
    source_location_id: unknown;
    dest_location_id: unknown;
  };
  return {
    ...transfer,
    id: canonicalStockyPublicBigIntId(transfer.id),
    source_location_id:
      transfer.source_location_id === null
        ? null
        : canonicalStockyPublicBigIntId(transfer.source_location_id),
    dest_location_id:
      transfer.dest_location_id === null
        ? null
        : canonicalStockyPublicBigIntId(transfer.dest_location_id),
  };
}

export async function listInventoryTransfers(): Promise<TransferSummary[]> {
  const data = await apiFetch<{ transfers: unknown[] }>(
    "/api/inventory-transfers",
  );
  if (!Array.isArray(data.transfers)) {
    throw new TypeError("inventory transfer list response is invalid");
  }
  return data.transfers.map(normalizeTransferSummary);
}

export async function getInventoryTransfer(
  id: StockyPublicBigIntId,
): Promise<Transfer> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  return normalizeTransfer(
    await apiFetch<unknown>(
      `/api/inventory-transfers/${encodeURIComponent(canonicalId)}`,
    ),
  );
}

export async function createInventoryTransfer(input: {
  source_location_id: StockyPublicBigIntId;
  dest_location_id: StockyPublicBigIntId;
  note?: string;
  items: { shopify_variant_id: PublicBigIntIdInput; qty: number }[];
}, idempotencyKey: string): Promise<Transfer> {
  const body = {
    ...input,
    source_location_id: canonicalStockyPublicBigIntId(input.source_location_id),
    dest_location_id: canonicalStockyPublicBigIntId(input.dest_location_id),
    items: input.items.map((item) => ({
      ...item,
      shopify_variant_id: canonicalPublicBigIntId(
        item.shopify_variant_id,
        "shopify_variant_id",
      ),
    })),
  };
  const response = await apiFetch<unknown>("/api/inventory-transfers", {
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return normalizeTransfer(response);
}

export async function shipInventoryTransfer(
  id: StockyPublicBigIntId,
  idempotencyKey: string,
): Promise<Transfer> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const response = await apiFetch<unknown>(
    `/api/inventory-transfers/${encodeURIComponent(canonicalId)}/ship`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
  return normalizeTransfer(response);
}

export async function receiveInventoryTransfer(
  id: StockyPublicBigIntId,
  receipts: { shopify_variant_id: PublicBigIntIdInput; qty: number }[],
  idempotencyKey: string,
): Promise<Transfer> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const canonicalReceipts = receipts.map((receipt) => ({
    ...receipt,
    shopify_variant_id: canonicalPublicBigIntId(
      receipt.shopify_variant_id,
      "shopify_variant_id",
    ),
  }));
  const response = await apiFetch<unknown>(
    `/api/inventory-transfers/${encodeURIComponent(canonicalId)}/receive`,
    {
      method: "POST",
      body: { receipts: canonicalReceipts },
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
  return normalizeTransfer(response);
}

export async function cancelInventoryTransfer(
  id: StockyPublicBigIntId,
  idempotencyKey: string,
): Promise<Transfer> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const response = await apiFetch<unknown>(
    `/api/inventory-transfers/${encodeURIComponent(canonicalId)}/cancel`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
  return normalizeTransfer(response);
}
